import React, { createContext, useContext, useEffect, useId, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Divider, FormControlLabel, FormGroup, Grid, IconButton, Switch } from '@mui/material';
import TextField from '@mui/material/TextField';
import useApi from '../../../shared/hooks/useApi';
import CloseIcon from '@mui/icons-material/Close';
import { Tabs, Tab } from '@mui/material';

const SettingsModal = ({ open, onClose, setLoading, triggerAlert }) => {
    const [settings, setSettings] = useState({});
    const [emailSettings, setEmailSettings] = useState({});
    const [originalSettings, setOriginalSettings] = useState({});
    const { apiPost, apiGet, loadingApi } = useApi();
    const allowedRoles = localStorage.getItem('all_roles');
    const [currentTab, setCurrentTab] = useState(0);
    const [roleLabelPairs, setRoleLabelPairs] = useState({});

   
    const roleBasedSettings = Object.entries(settings).reduce((acc, [key, setting]) => {
        const { role, category } = setting;
        if (role === 'email') return acc;
        if (!acc[role]) {
            acc[role] = {};
        }
        if (!acc[role][category]) {
            acc[role][category] = [];
        }
        acc[role][category].push({ key, ...setting });
        return acc;
    }, {});

    // Sort roles with operator first, then alphabetically
    const roles = Object.keys(roleBasedSettings).sort((a, b) => {
        if (a.toLowerCase().includes('operator')) return -1;
        if (b.toLowerCase().includes('operator')) return 1;
        return a.localeCompare(b);
    });
    
    // Add email tab if email settings exist
    const allTabs = [...roles];
    if (Object.keys(emailSettings).length > 0) {
        allTabs.push('email');
    }
    

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const closeModal = () => {
        onClose();
    }

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await apiGet("UX_HTTP_API", `/esid-24-031/get-settings`);
            // Set settings and email settings separately
            setSettings(response.settings || {});
            
            // Extract role and role_label pairs from settings
            const rolePairs = {};
            if (response.settings) {
                Object.values(response.settings).forEach(setting => {
                    if (setting.role && setting.role_label) {
                        rolePairs[setting.role] = setting.role_label;
                    }
                });
            }
            setRoleLabelPairs(rolePairs);
            
            setEmailSettings(response.email_settings || {});
            setOriginalSettings(response.settings || {});
        
        } catch (error) {
            console.error("Error fetching settings:", error);
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchSettings();
    }, [allowedRoles]);


    const handleChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                value: value
            }
        }));
    };

    const handleEmailChange = (key, value) => {
        setEmailSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const isDisabled = (key) => {
        return settings[key]?.disabled || false;
    };

    const changeToggle = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: { ...prev[key], value } }));
    };
    
    const handleSave = async () => {
        try {
            setLoading(true);
            const simplifySettings = (detailedSettings) => {
                const simplifiedObj = Object.entries(detailedSettings).reduce((acc, [key, details]) => {
                    // Only include actual settings, not email settings
                    if (details.role && details.role !== 'email') {
                        acc[key] = details.value;
                    }
                    return acc;
                }, {});
                return simplifiedObj;
            }

            // Save both settings and email settings
            await apiPost("UX_HTTP_API", "/esid-24-031/update-settings", { 
                settings: simplifySettings(settings),
                email_settings: emailSettings
            });
            setOriginalSettings(settings);
            await fetchSettings();
            onClose();

        } catch (error) {
            console.error("Error saving settings:", error);
            triggerAlert("error", "Error saving settings");
            setSettings(originalSettings);
        } finally {
            setLoading(false);
        }
    };

    // Get role display name
    const getRoleDisplayName = (role) => {
        return roleLabelPairs[role] || role.replace('_', ' ');
    };

    // Email settings fields configuration
    const emailFields = [
        { key: 'source', label: 'From Email', type: 'email' },
        { key: 'emailTo', label: 'To Email', type: 'email' },
        { key: 'emailCC', label: 'CC Email', type: 'email' },
        { key: 'subject', label: 'Subject', type: 'text' },
        { key: 'body', label: 'Email Body', type: 'textarea' }
    ];

    return (
        <Modal open={open} onClose={closeModal}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                maxWidth: 900,
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                zIndex: 998,
                borderRadius: 2,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '10px',
                    backgroundColor: '#f5f5f5',
                },
                '&::-webkit-scrollbar-track': {
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                },
                '&::-webkit-scrollbar-thumb': {
                    borderRadius: '8px',
                    backgroundColor: 'rgba(72,63,94,0.2)',
                    '&:hover': {
                        backgroundColor: 'rgba(72,63,94,0.4)',
                    },
                },
            }}>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Settings Configuration
                    </Typography>
                    <IconButton
                        onClick={closeModal}
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 1000,
                        }}
                    >
                        <CloseIcon fontSize="inherit"
                            sx={{ color: "black" }} />
                    </IconButton>
                </Box>

                <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        mb: 2
                    }}
                >
                    {allTabs.map((role, index) => (
                        <Tab
                            key={role}
                            label={getRoleDisplayName(role)}
                            sx={{
                                textTransform: 'capitalize',
                                fontWeight: 'medium'
                            }}
                        />
                    ))}
                </Tabs>
                
                {/* Settings content for current tab */}
                {allTabs.map((role, index) => (
                    <Box
                        key={role}
                        hidden={currentTab !== index}
                        sx={{ mt: 2 }}
                    >
                        {role === 'email' ? (
                            // Email settings tab
                            <Box>
                                <Typography variant="h6" sx={{ mt: 2, mb: 2, ml: 1 }} component="h2" gutterBottom>
                                    Email Configuration
                                </Typography>
                                <Grid container spacing={2}>
                                    {emailFields.map(({ key, label, type }) => (
                                        <Grid item xs={12} md={type === 'textarea' ? 12 : 6} key={key}>
                                            <TextField
                                                label={label}
                                                value={emailSettings[key] || ''}
                                                onChange={(e) => handleEmailChange(key, e.target.value)}
                                                fullWidth
                                                margin="normal"
                                                type={type === 'email' ? 'email' : 'text'}
                                                multiline={type === 'textarea'}
                                                rows={type === 'textarea' ? 4 : 1}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ) : (
                            // Regular settings tabs
                            <>
                                {Object.entries(roleBasedSettings[role] || {}).map(([category, items]) => (
                                    <React.Fragment key={category}>
                                        <Typography variant="h6" sx={{ mt: 2, mb: 2, ml: 1 }} component="h2" gutterBottom>
                                            {category}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {items.map(({ key, label, description, value, datatype, unit, default_value }) => (
                                                <Grid item xs={6} md={4} key={key}>
                                                    {datatype === 'Boolean' ? (
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={value || false}
                                                                    onChange={(e) => changeToggle(key, e.target.checked)}
                                                                />
                                                            }
                                                            label={label}
                                                            title={description}
                                                            sx={{ my: 1, mr: 1, pt: 2 }}
                                                        />
                                                    ) : (
                                                        <TextField
                                                            label={label}
                                                            value={value ?? ''}
                                                            onChange={(e) => {
                                                                const newValue = datatype === 'Float' || datatype === 'Integer'
                                                                    ? parseFloat(e.target.value) || 0
                                                                    : e.target.value;
                                                                handleChange(key, newValue);
                                                            }}
                                                            disabled={isDisabled(key)}
                                                            fullWidth
                                                            margin="normal"
                                                            helperText={`${description}${unit ? ` (${unit})` : ''}`}
                                                            type={datatype === 'Float' || datatype === 'Integer' ? 'number' : 'text'}
                                                            InputProps={{
                                                                step: datatype === 'Float' ? 'any' : '1'
                                                            }}
                                                        />
                                                    )}
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                    </Box>
                ))}
                

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={closeModal} sx={{ width: '100px' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loadingApi}
                        sx={{ width: '100px', bgcolor: '#1F9562' }}
                    >
                        {loadingApi ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default SettingsModal;